import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { BookOpen, ArrowRight, HeartHandshake, Baby, Gift, Sparkles, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { resolveImageUrl } from '@/lib/image';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = { BookOpen, HeartHandshake, Baby, Gift, Sparkles, Heart };

// Fallback icon by category slug when no icon is set on the category
const SLUG_ICON = {
  'expecting-new-moms': HeartHandshake,
  'toddlers': Baby,
  'non-toy-gifts-for-kids': Gift,
};

export default function HomePage() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Add books as a card
  const allCategories = [
    {
      title: "Children's Books",
      description: 'Discover stories that spark imagination and build character',
      image: 'https://images.unsplash.com/photo-1532774682361-95f0831019d7?w=800',
      link: '/books',
      icon: BookOpen,
      testId: 'home-books-card',
    },
    ...categories.map(cat => ({
      title: cat.name,
      description: cat.description,
      image: cat.image_url || 'https://images.unsplash.com/photo-1646221754706-df41ef35e852?w=800',
      link: `/gifts/${cat.slug}`,
      icon: ICON_MAP[cat.icon] || SLUG_ICON[cat.slug] || Gift,
      testId: `home-${cat.slug}-card`,
    }))
  ];

  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 px-6 md:px-12 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-6">
              <p className="font-accent text-accent text-xl md:text-2xl" data-testid="hero-tagline">
                Thoughtfully curated
              </p>
              <h1
                className="font-heading text-5xl md:text-7xl font-light tracking-tight leading-[1.1] text-primary"
                data-testid="hero-title"
              >
                Meaningful Gifts & Children's Literature
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-muted-foreground font-light" data-testid="hero-description">
                Stop guessing what to gift. We've done the thinking for you — curated recommendations that are
                developmentally appropriate, genuinely thoughtful, and always meaningful.
              </p>
              <div className="flex gap-4 pt-4">
                <Link to="/books">
                  <Button className="rounded-full shadow-md hover:shadow-lg" size="lg" data-testid="hero-explore-books-button">
                    Explore Books
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="rounded-full" size="lg" data-testid="hero-why-curate-button">
                    Why We Curate
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-2xl" data-testid="hero-image">
              <img
                src="https://images.unsplash.com/photo-1532774682361-95f0831019d7?w=1200&q=80"
                alt="Mother and child reading together"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-primary mb-4" data-testid="categories-title">
              Explore Our Curations
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="categories-description">
              Each recommendation is chosen with care, context, and an understanding of what truly matters.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {allCategories.map((category) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.link}
                  to={category.link}
                  className="group"
                  data-testid={category.testId}
                >
                  <div className="bg-card border border-border/50 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden h-full">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={resolveImageUrl(category.image)}
                        alt={category.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-4 left-4 text-white">
                        <Icon className="h-8 w-8" />
                      </div>
                    </div>
                    <div className="p-6 space-y-3">
                      <h3 className="font-heading text-2xl font-normal text-foreground group-hover:text-primary transition-colors">
                        {category.title}
                      </h3>
                      <p className="text-base text-muted-foreground leading-relaxed">{category.description}</p>
                      <div className="flex items-center text-accent font-body text-sm font-semibold pt-2">
                        Explore <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight" data-testid="cta-title">
            Not Sure Where to Start?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed opacity-90" data-testid="cta-description">
            Every recommendation comes with context — why it matters, when to give it, and what makes it special.
          </p>
          <Link to="/search">
            <Button
              variant="secondary"
              size="lg"
              className="rounded-full mt-4"
              data-testid="cta-search-button"
            >
              Search All Recommendations
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}